import type { GameState } from './gameState';
import type { Team } from './team';
import type { Worm } from '../entities/worm';

/**
 * In-match consumable items (GunBound-style). Items are separate from
 * weapons: using one is instant, does not end the turn, and costs turn
 * delay instead of ammo. Per-team counts come from mode.items.matchItems.
 */
export interface MatchItemDefinition {
  id: string;
  name: string;
  /** Short glyph for the item bar button. */
  icon: string;
  description: string;
  /** Turn delay added when used (tracked in delay mode). */
  delay: number;
}

export const MATCH_ITEMS: Record<string, MatchItemDefinition> = {
  dual: {
    id: 'dual',
    name: 'Dual',
    icon: '2\u00d7',
    description: 'Your next shot fires twice',
    delay: 600,
  },
  powerUp: {
    id: 'powerUp',
    name: 'Power-Up',
    icon: '\u26a1',
    description: '+33% damage on your next shot',
    delay: 450,
  },
  blood: {
    id: 'blood',
    name: 'Blood',
    icon: '\u{1fa78}',
    description: 'Sacrifice 8% HP: +50% damage on your next shot',
    delay: 0,
  },
  energyUp: {
    id: 'energyUp',
    name: 'Energy-Up',
    icon: '\u2764',
    description: 'Heal 25% of max HP',
    delay: 550,
  },
  changeWind: {
    id: 'changeWind',
    name: 'Wind Change',
    icon: '\u{1f300}',
    description: 'Re-roll the wind',
    delay: 350,
  },
};

export interface ItemUseContext {
  state: GameState;
  team: Team;
  worm: Worm;
}

/**
 * Validate and apply a match item for the current turn: decrements the
 * team's count, applies the effect, and charges the item's delay cost.
 * Shared by the offline game and the authoritative server.
 */
export function useMatchItem(itemId: string, ctx: ItemUseContext): boolean {
  const def = MATCH_ITEMS[itemId];
  if (!def) return false;
  if (ctx.state.phase !== 'playing') return false;
  if (ctx.state.itemUsedThisTurn) return false; // one item per turn
  if (!ctx.team.useItem(itemId)) return false;
  ctx.state.itemUsedThisTurn = true;

  switch (itemId) {
    case 'dual':
      ctx.state.grantExtraShot();
      break;
    case 'powerUp':
      ctx.state.boostShotDamage(4 / 3);
      break;
    case 'blood': {
      const cost = Math.round(ctx.worm.maxHealth * 0.08);
      ctx.worm.health = Math.max(1, ctx.worm.health - cost);
      ctx.state.boostShotDamage(1.5);
      break;
    }
    case 'energyUp': {
      const heal = Math.round(ctx.worm.maxHealth * 0.25);
      ctx.worm.health = Math.min(ctx.worm.maxHealth, ctx.worm.health + heal);
      break;
    }
    case 'changeWind':
      ctx.state.rerollWind();
      break;
  }

  ctx.state.addTurnDelay(def.delay);
  return true;
}
